/**
 * Block 13 PR-G Phase 6.5D — Language picker (native HTML `<select>`).
 *
 * **Phase 6.5D Item #1 (Aibek decision Q1 = Вариант Б, 2026-05-24):** заменён
 * `<ChipSegment>` (chip-radio pattern) на native `<select>` для предсказуемого
 * dropdown UX. Available languages для текущего региона:
 *   - KZ: {Русский / Қазақша}
 *   - RU: hidden (single language)
 *   - UZ: {Русский / Oʻzbek}
 *
 * **Region-locked design (#1 investigation 2026-05-26).** UZ option
 * рендерится **только** для UZ-region пользователей (т.е. зарегистрировался
 * через onboarding с городом «Ташкент» → CITY_REGION_MAP → region='UZ').
 * KZ пользователь не видит UZ option — это by design (KZ-договор не может
 * быть на узбекском языке).
 *
 * Прозрачно дёргает `SET_TEMPLATE_KEY` action на reducer через `onChange` —
 * меняет `templateKey` (например kz.ru → kz.kk).
 *
 * PRD §10.13.2 «Язык договора».
 *
 * Reference:
 *   - ADR-0025 §Q1 (5 templates)
 *   - packages/shared/src/domain/onboarding-vocabularies.ts CITY_REGION_MAP
 */

import {
  CONTRACT_LANGUAGES_PER_REGION,
  buildContractTemplateKey,
  type ContractLanguage,
  type ContractRegion,
  type ContractTemplateKey,
} from '@pomoshnik/shared';

/** Display labels для select option — Russian-language UI везде, label языка на самом языке. */
const LANGUAGE_LABELS: Record<ContractLanguage, string> = {
  ru: 'Русский',
  kk: 'Қазақша',
  uz: 'Oʻzbek',
};

const LABEL_CLASS = 'block text-sm font-semibold text-ink/80 mb-1';
const SELECT_CLASS =
  'w-full min-h-tap px-3 pr-10 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
  // native dropdown chevron preserved через default browser appearance; explicit
  // appearance-auto чтобы Tailwind preflight reset не убрал arrow.
  'appearance-auto';

export interface LanguagePickerProps {
  region: ContractRegion;
  templateKey: ContractTemplateKey;
  onChange: (next: ContractTemplateKey) => void;
}

/**
 * Если регион имеет одну language (RU) — компонент рендерит `null`. Caller
 * (SectionPreamble) скрывает label «Язык договора» соответственно через
 * truthy check на children.
 */
export function LanguagePicker({ region, templateKey, onChange }: LanguagePickerProps) {
  const languages = CONTRACT_LANGUAGES_PER_REGION[region];
  if (languages.length < 2) return null;

  // Extract current lang из templateKey ("kz.ru" → "ru").
  const currentLang = (templateKey.split('.')[1] ?? languages[0]) as ContractLanguage;

  return (
    <div>
      <label htmlFor="contract-language-select" className={LABEL_CLASS}>
        Выбери язык договора
      </label>
      <select
        id="contract-language-select"
        value={currentLang}
        onChange={(e) =>
          onChange(buildContractTemplateKey(region, e.target.value as ContractLanguage))
        }
        // a11y: <label htmlFor="contract-language-select"> уже provides accessible
        // name «Выбери язык договора» — explicit aria-label больше не нужен (был
        // бы redundant override на screen reader announce).
        data-testid="contract-language-select"
        className={SELECT_CLASS}
      >
        {languages.map((lang) => (
          <option key={lang} value={lang}>
            {LANGUAGE_LABELS[lang]}
          </option>
        ))}
      </select>
    </div>
  );
}
